import { LocationSummary } from "@/types/place"
import { PublicUser, UserRef } from "@/types/user"
import { createAsyncThunk } from "@reduxjs/toolkit"

import { buildClientAxios } from "../client-axios"
import { UserApi } from "../user-api"
import { placeApi } from "./api/place-api"
import publicUserSlice, {
  selectPublicUserLocationsByRef,
  selectPublicUsersByRef,
} from "./public-user-slice"
import { AppDispatch, RootState } from "./redux-store"
import userSlice, { selectCurrentUserRef } from "./user-slice"

// The follow counts ride along inside the user payload, so record them the moment
// it lands, before anything else this thunk awaits. Consumers can tell a missing
// record from a real zero, so a late write is not wrong — it just means the count
// appears a beat after the name and resizes the card. Recording nothing at all is
// reserved for counts genuinely absent from the payload.
function recordFollowCounts(
  user: PublicUser,
  currentUserRef: UserRef | undefined,
  dispatch: AppDispatch
) {
  const profile = user.user_profile
  if (!profile) return

  const isCurrentUser = currentUserRef === user.user_ref
  const followers = isCurrentUser
    ? profile.followers_count
    : profile.accepted_followers_count

  // A profile can arrive without any counts on it. Recording 0 for those would
  // be indistinguishable from a real zero, which is the whole thing consumers
  // need to be able to tell apart, so leave the counts unrecorded instead.
  if (followers === undefined && profile.following_count === undefined) return

  dispatch(
    userSlice.actions.setFollowCounts({
      userRef: user.user_ref,
      followers: followers ?? 0,
      following: profile.following_count ?? 0,
    })
  )
}

export const fetchPublicUsers = createAsyncThunk<
  PublicUser[],
  { userRefs: UserRef[] },
  {
    dispatch: AppDispatch
    state: RootState
  }
>(
  "publicUserThunks/fetchPublicUsers",
  async ({ userRefs }, { dispatch, getState }) => {
    const publicUsersByRef = selectPublicUsersByRef(getState())
    const userRefsToLoad: UserRef[] = []
    const users: PublicUser[] = []

    userRefs.forEach((userRef) => {
      const user = publicUsersByRef.get(userRef)
      if (user) {
        users.push(user)
      } else {
        userRefsToLoad.push(userRef)
      }
    })

    const userApi = new UserApi(buildClientAxios())
    if (userRefsToLoad.length > 0) {
      try {
        const loadedUsers = await userApi.fetchPublicUsersByRef(userRefsToLoad)
        users.push(...loadedUsers)
        dispatch(publicUserSlice.actions.addPublicUsers(loadedUsers))
        const currentUserRef = selectCurrentUserRef(getState())
        loadedUsers.forEach((user) =>
          recordFollowCounts(user, currentUserRef, dispatch)
        )
      } catch (error) {
        console.error("Error fetching users", userRefsToLoad, error)
      }
    }

    return users
  }
)

export const fetchPublicUser = createAsyncThunk<
  { publicUser: PublicUser | undefined; location: LocationSummary | undefined },
  { userRef: UserRef },
  { dispatch: AppDispatch; state: RootState }
>(
  "publicUserThunks/fetchPublicUser",
  async ({ userRef }, { dispatch, getState }) => {
    const users = await dispatch(
      fetchPublicUsers({ userRefs: [userRef] })
    ).unwrap()

    // The fetch swallows its own errors, so there may be no user to work with.
    const publicUser = users[0]
    if (!publicUser) {
      return { publicUser: undefined, location: undefined }
    }

    // Re-record the counts even on a cache hit: the cached user may have been
    // loaded before sign-in, and which follower count applies depends on whether
    // the viewer is looking at themselves.
    recordFollowCounts(publicUser, selectCurrentUserRef(getState()), dispatch)

    let locationSummary: LocationSummary | undefined = undefined
    const locationRefId = publicUser.user_profile?.location
    if (locationRefId) {
      const publicUserLocationsByRef =
        selectPublicUserLocationsByRef(getState())
      locationSummary = publicUserLocationsByRef.get(locationRefId)
      if (!locationSummary) {
        // The location is a second request and only decorates the card, so a
        // failure here must not discard the user we already resolved.
        try {
          locationSummary = await dispatch(
            placeApi.endpoints.getLocation.initiate(locationRefId)
          ).unwrap()
          dispatch(
            publicUserSlice.actions.addPublicUserLocations([locationSummary])
          )
        } catch (error) {
          console.error("Error fetching user location", locationRefId, error)
        }
      }
    }

    return { publicUser, location: locationSummary }
  }
)
